import { FrontendDiagnosticsService, getSparkClientContext } from './core/services/frontend-diagnostics.service';
import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';

import { environment } from '../environments/environment';
import { AlertService } from './shared/services/alert.service';
import { AuthService } from './auth';
import {
  UserModel,
  GeneralSettingsModel,
  JwTokenModel,
  Permissions,
} from './core/api/models';
import {
  ActivatedRoute,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { AccountApiService } from './core/api/services';
import { OfflineDataService } from './core/services/offline-data.service';
import { MiniProfilerService } from './core/services/mini-profiler.service';
import { AppVersionService } from './core/services/app-version.service';
import { SparkNativeBridgeService } from './core/services/spark-native-bridge.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.css',
  standalone: false,
})
export class AppComponent implements OnInit {
  public isSignedIn: boolean = false;
  private subscription!: Subscription;
  message: any = null;
  showUpdateDialog = false;
  updateError = '';
  private promptedVersion: string | null = null;

  logoURL: string = '/assets/OPTO Logo.png';

  userInfo: UserModel | null = null;
  generalSettings: GeneralSettingsModel | null = null;


  constructor(
    private http: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
    private alertService: AlertService,
    private messageService: MessageService,
    private authService: AuthService,
    private accountApiService: AccountApiService,
    private offlineDataService: OfflineDataService,
    private profiler: MiniProfilerService,
    public appVersionService: AppVersionService,
    _sparkNativeBridge: SparkNativeBridgeService,
    private diagnostics: FrontendDiagnosticsService
    
  ) {
    console.debug('AppComponent.constructor');
  }

  ngOnInit() {
    this.diagnostics.recordEntry('entry-loaded');
    if (!environment.production)
      this.profiler.init();


    this.authService.getSettings().subscribe((settings) => {
      this.generalSettings = settings;
      this.logoURL = settings?.BusinessLogoUrl || '/assets/OPTO Logo.png';
    });

    this.subscription = this.alertService.getMessage().subscribe((message) => {
      this.message = message;
    });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationError) {
        console.error(event.error);
        this.router.navigate(['/'], {
          queryParams: { errorMessage: event.error?.message },
        });
        this.alertService.error(event.error?.message ?? 'Navigation error');
        return;
      }
      if (event instanceof NavigationEnd) {
        if (/^\/(staff|supervisor)\//.test(event.urlAfterRedirects)) {
          this.diagnostics.recordEntry('destination-route-activated', {
            Route: event.urlAfterRedirects.split('?')[0].replace(/\d+/g, ':id'),
            HasAccessToken: this.authService.isSignedIn(),
            AccessRemainingMs: this.authService.accessTokenRemainingMs(),
          });
        }
        let urlTree = this.router.parseUrl(event.urlAfterRedirects);
        const safeParameters = { ...urlTree.queryParams };
        delete safeParameters['token'];
        let parameters = JSON.stringify(safeParameters);
        urlTree.queryParams = {};
        let cleanUrl = urlTree.toString();

        if (this.authService.userInfo?.Permissions != null
          && !cleanUrl.startsWith('/not-authorized')
          && !cleanUrl.startsWith('/auth/')
          && !cleanUrl.startsWith('/parent/')) {
          const permissions = this.authService.userInfo.Permissions;
          const isSparkUser =
            permissions.includes(Permissions.SparkApp_Staff) ||
            permissions.includes(Permissions.SparkApp_Supervisor);
          if (!isSparkUser) {
            this.router.navigate(['/not-authorized']);
          }
        }

        let context = JSON.stringify({
          SparkClient: getSparkClientContext(),
          AppVersion: this.appVersionService.getVersionRaw(),
          UserID: this.authService.userInfo?.ID,
          TokenExpiresAt: this.authService.tokenInfo?.expires_at,
          RefreshExpiresAt: this.authService.tokenInfo?.refresh_expires_at,
          //localStorage.getItem('currentUser');
        });

        if (!this.offlineDataService.isInOfflineMode()) {
          this.accountApiService
            .LogNavigation({
              body: {
                Url: cleanUrl,
                Parameters: parameters,
                Method: 'ROUTE',
                Context: context,
              },
            })
            .forEach(() => { });
        }
        (window as any).NavigationRouterState = 'NavigationEnd: ' + cleanUrl;
      } else if (event instanceof NavigationStart) {
        (window as any).NavigationRouterState = 'NavigationStart';
        if (!environment.production) {
          this.profiler.clear();
        }

      } else if (event instanceof NavigationCancel) {
        (window as any).NavigationRouterState = 'NavigationCancel';
      } else if (event instanceof NavigationError) {
        (window as any).NavigationRouterState = 'NavigationError';
      }
      console.debug((window as any).NavigationRouterState);
    });

    this.subscription.add(this.appVersionService.updateAvailable$().subscribe(hasUpdate => {
      const latest = this.appVersionService.getLatestVersionRaw() + (this.appVersionService.recoveryRequired ? '|repair' : '');
      if (hasUpdate && latest !== this.promptedVersion) {
        this.promptedVersion = latest;
        this.updateError = '';
        this.showUpdateDialog = true;
      } else if (!hasUpdate) {
        this.showUpdateDialog = false;
      }
    }));
    this.subscription.add(this.appVersionService.promptRequests.subscribe(() => {
      this.updateError = '';
      this.showUpdateDialog = true;
    }));
    this.appVersionService.initialize();

  }

  async updateApp(): Promise<void> {
    this.updateError = '';
    try {
      const updated = await this.appVersionService.applyUpdate();
      if (!updated) this.showUpdateDialog = false;
    } catch (error) {
      this.updateError = error instanceof Error ? error.message : String(error);
    }
  }

  ngAfterViewInit(): void {
    console.debug('AppComponent.AfterViewInit');
  }
  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
  onConfirm() {
    this.messageService.clear('confirmToat');

    this.alertService.$confirm.next(true);

    this.alertService.$confirm.complete();
  }
  onReject() {
    this.messageService.clear('confirmToat');
    this.messageService.clear('optionToast');
    this.alertService.$confirm.next(false);
    this.alertService.$option.next('');

    this.alertService.$confirm.complete();
    this.alertService.$option.complete();
  }
  clearMessage() {
    this.messageService.clear('confirmToat');
    this.messageService.clear('optionToast');
    this.message = null;
  }
  onOptionSelected(option: string) {
    this.messageService.clear('optionToast');
    this.alertService.$option.next(option);
    this.alertService.$option.complete();
  }

}
